// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Curry from "rescript/lib/es6/curry.js";
import * as Js_array from "rescript/lib/es6/js_array.js";
import * as Belt_List from "rescript/lib/es6/belt_List.js";
import * as Belt_Result from "rescript/lib/es6/belt_Result.js";
import * as Caml_option from "rescript/lib/es6/caml_option.js";

var running = {
  contents: false
};

var callbacks = [];

function runLoop(_callback) {
  while(true) {
    var callback = _callback;
    Curry._1(callback, undefined);
    var callback$1 = callbacks.pop();
    if (callback$1 === undefined) {
      return ;
    }
    _callback = callback$1;
    continue ;
  };
}

function trampoline(callback) {
  if (running.contents) {
    Js_array.unshift(callback, callbacks);
  } else {
    running.contents = true;
    runLoop(callback);
    running.contents = false;
  }
}

function make(executorOpt, resolver) {
  var executor = executorOpt !== undefined ? executorOpt : "none";
  var callbacks = {
    contents: /* [] */0
  };
  var data = {
    contents: undefined
  };
  var runCallback = executor === "trampoline" ? (function (result, cb) {
        trampoline(function (param) {
              Curry._1(cb, result);
            });
      }) : (function (result, cb) {
        Curry._1(cb, result);
      });
  Curry._1(resolver, (function (result) {
          var match = data.contents;
          if (match !== undefined) {
            return ;
          } else {
            data.contents = Caml_option.some(result);
            Belt_List.forEach(Belt_List.reverse(callbacks.contents), Curry._1(runCallback, result));
            callbacks.contents = /* [] */0;
            return ;
          }
        }));
  return /* Future */{
          _0: (function (resolve) {
              var result = data.contents;
              if (result !== undefined) {
                return Curry._2(runCallback, Caml_option.valFromOption(result), resolve);
              } else {
                callbacks.contents = {
                  hd: resolve,
                  tl: callbacks.contents
                };
                return ;
              }
            }),
          _1: executor
        };
}

function value(executorOpt, x) {
  var executor = executorOpt !== undefined ? executorOpt : "none";
  return make(executor, (function (resolve) {
                Curry._1(resolve, x);
              }));
}

function map(param, f) {
  var get = param._0;
  return make(param._1, (function (resolve) {
                Curry._1(get, (function (result) {
                        Curry._1(resolve, Curry._1(f, result));
                      }));
              }));
}

function flatMap(param, f) {
  var get = param._0;
  return make(param._1, (function (resolve) {
                Curry._1(get, (function (result) {
                        var match = Curry._1(f, result);
                        Curry._1(match._0, resolve);
                      }));
              }));
}

function map2(fa, fb, f) {
  return flatMap(fa, (function (a) {
                return map(fb, (function (b) {
                              return Curry._2(f, a, b);
                            }));
              }));
}

function map3(fa, fb, fc, f) {
  return map2(map2(fa, fb, f), fc, (function (v) {
                return v;
              }));
}

function map4(fa, fb, fc, fd, f) {
  return map3(map2(fa, fb, f), fc, fd, (function (v) {
                return v;
              }));
}

function map5(fa, fb, fc, fd, fe, f) {
  return map4(map2(fa, fb, f), fc, fd, fe, (function (v) {
                return v;
              }));
}

function all(futures) {
  if (!futures) {
    return value(undefined, /* [] */0);
  }
  var head = futures.hd;
  return flatMap(all(futures.tl), (function (tailResult) {
                return map(head, (function (headResult) {
                              return {
                                      hd: headResult,
                                      tl: tailResult
                                    };
                            }));
              }));
}

function tap(future, f) {
  Curry._1(future._0, f);
  return future;
}

function get(param, f) {
  Curry._1(param._0, f);
}

function mapOk(future, f) {
  return map(future, (function (r) {
                return Belt_Result.map(r, f);
              }));
}

function mapError(future, f) {
  return map(future, (function (r) {
                if (r.TAG === /* Ok */0) {
                  return {
                          TAG: /* Ok */0,
                          _0: r._0
                        };
                } else {
                  return {
                          TAG: /* Error */1,
                          _0: Curry._1(f, r._0)
                        };
                }
              }));
}

function flatMapOk(future, f) {
  return flatMap(future, (function (r) {
                if (r.TAG === /* Ok */0) {
                  return Curry._1(f, r._0);
                } else {
                  return value(undefined, {
                              TAG: /* Error */1,
                              _0: r._0
                            });
                }
              }));
}

function flatMapError(future, f) {
  return flatMap(future, (function (r) {
                if (r.TAG === /* Ok */0) {
                  return value(undefined, {
                              TAG: /* Ok */0,
                              _0: r._0
                            });
                } else {
                  return Curry._1(f, r._0);
                }
              }));
}

function mapOk2(fa, fb, f) {
  return flatMapOk(fa, (function (a) {
                return mapOk(fb, (function (b) {
                              return Curry._2(f, a, b);
                            }));
              }));
}

function mapOk3(fa, fb, fc, f) {
  return mapOk2(mapOk2(fa, fb, f), fc, (function (v) {
                return v;
              }));
}

function mapOk4(fa, fb, fc, fd, f) {
  return mapOk3(mapOk2(fa, fb, f), fc, fd, (function (v) {
                return v;
              }));
}

function mapOk5(fa, fb, fc, fd, fe, f) {
  return mapOk4(mapOk2(fa, fb, f), fc, fd, fe, (function (v) {
                return v;
              }));
}

function tapOk(future, f) {
  return tap(future, (function (r) {
                if (r.TAG !== /* Ok */0) {
                  return ;
                }
                Curry._1(f, r._0);
              }));
}

function tapError(future, f) {
  return tap(future, (function (r) {
                if (r.TAG === /* Ok */0) {
                  return ;
                }
                Curry._1(f, r._0);
              }));
}

function delay(executor, ms, f) {
  return make(executor, (function (resolve) {
                setTimeout((function (param) {
                        Curry._1(resolve, Curry._1(f, undefined));
                      }), ms);
              }));
}

function sleep(executor, ms) {
  return delay(executor, ms, (function (param) {
                
              }));
}

var $great$great$eq = flatMapOk;

var $less$$great = mapOk;

export {
  trampoline ,
  make ,
  value ,
  map ,
  flatMap ,
  map2 ,
  map3 ,
  map4 ,
  map5 ,
  all ,
  tap ,
  get ,
  mapOk ,
  mapError ,
  flatMapOk ,
  flatMapError ,
  mapOk2 ,
  mapOk3 ,
  mapOk4 ,
  mapOk5 ,
  tapOk ,
  tapError ,
  delay ,
  sleep ,
  $great$great$eq ,
  $less$$great ,
}
/* No side effect */
